'use client';

import { useContext, createContext, useEffect } from 'react';

import { useAuiState } from '@assistant-ui/react';
import { useEvent } from 'react-use';

import { useSafePostHog } from '@/hooks/use-safe-posthog';
import { useTrackEvent } from '@/hooks/use-track-event';
import { ABORT_REASONS, CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import { getThreadUuid } from '@/lib/utils/thread';

import {
  cleanupPendingResponse,
  getIsRetry,
  type ResponseTrackingContextValue,
} from './analytics-utils';

export type { ResponseTrackingContextValue };

export const ResponseTrackingContext = createContext<ResponseTrackingContextValue | null>(null);

const QUEUED_EVENTS_KEY = 'queued-analytics-events';

interface QueuedEvent {
  event: string;
  properties: Record<string, unknown>;
  timestamp: number;
}

// In-memory fallback when localStorage is unavailable
let memoryQueue: QueuedEvent[] = [];

// Exported for testing
export function _resetMemoryQueue(): void {
  memoryQueue = [];
}

export function _getMemoryQueue(): QueuedEvent[] {
  return memoryQueue;
}

export function _queueEventForLater(event: string, properties: Record<string, unknown>): void {
  queueEventForLater(event, properties);
}

function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

function queueEventForLater(event: string, properties: Record<string, unknown>): void {
  const queuedEvent: QueuedEvent = { event, properties, timestamp: Date.now() };

  if (isLocalStorageAvailable()) {
    try {
      const existing = localStorage.getItem(QUEUED_EVENTS_KEY);
      const queued: QueuedEvent[] = existing ? JSON.parse(existing) : [];
      queued.push(queuedEvent);
      localStorage.setItem(QUEUED_EVENTS_KEY, JSON.stringify(queued));
    } catch {
      memoryQueue.push(queuedEvent);
    }
  } else {
    memoryQueue.push(queuedEvent);
  }
}

interface FlushOptions {
  posthog: ReturnType<typeof useSafePostHog>;
  trackEvent: (params: { event: string; properties?: Record<string, unknown> }) => void;
}

function flushQueuedEvents({ posthog, trackEvent }: FlushOptions): void {
  if (!posthog) return; // guard: posthog initialization signals it's safe to flush

  const eventsToFlush: QueuedEvent[] = [];

  // Collect from localStorage if available
  if (isLocalStorageAvailable()) {
    try {
      const existing = localStorage.getItem(QUEUED_EVENTS_KEY);
      if (existing) {
        eventsToFlush.push(...JSON.parse(existing));
        localStorage.removeItem(QUEUED_EVENTS_KEY);
      }
    } catch {
      // Ignore storage errors
    }
  }

  // Collect from memory queue
  if (memoryQueue.length > 0) {
    eventsToFlush.push(...memoryQueue);
    memoryQueue = [];
  }

  // Send all queued events
  eventsToFlush.forEach(({ event, properties, timestamp }) => {
    trackEvent({
      event,
      properties: {
        ...properties,
        wasQueued: true,
        eventTimestamp: timestamp,
        queuedDurationMs: Date.now() - timestamp,
      },
    });
  });
}

export function useResponseTracking(): void {
  const posthog = useSafePostHog();
  const trackEvent = useTrackEvent();
  const context = useContext(ResponseTrackingContext);

  const rawThreadId = useAuiState(state => state.threadListItem?.id);
  const threadId = rawThreadId ? getThreadUuid(rawThreadId) : undefined;

  const messageStatus = useAuiState(({ thread }) => {
    const msg = thread.messages.at(-1);
    if (msg?.role !== 'assistant') return undefined;
    return msg.status?.type;
  });

  const messageStatusReason = useAuiState(({ thread }) => {
    const msg = thread.messages.at(-1);
    if (msg?.role !== 'assistant') return undefined;
    const status = msg.status;
    return status && 'reason' in status ? status.reason : undefined;
  });

  // Flush queued events on mount if online
  useEffect(() => {
    if (navigator.onLine && posthog) {
      flushQueuedEvents({ posthog, trackEvent });
    }
  }, [posthog, trackEvent]);

  // Flush queued events when coming back online
  useEvent('online', () => flushQueuedEvents({ posthog, trackEvent }));

  useEffect(() => {
    // Status types we handle:
    // - 'complete' = successful completion
    // - 'incomplete' with reason 'cancelled' = user clicked stop (no tool running)
    // - 'requires-action' with reason 'interrupt' = user clicked stop (tool was running)
    // - 'incomplete' with other reasons = error occurred
    const isTerminalStatus =
      messageStatus === 'complete' ||
      messageStatus === 'incomplete' ||
      (messageStatus === 'requires-action' && messageStatusReason === 'interrupt');

    if (!isTerminalStatus) return;
    if (!threadId || !context?.pendingResponsesRef.current) return;

    const startTime = context.pendingResponsesRef.current.get(threadId);
    if (!startTime) return;

    // Capture flags BEFORE cleanup (cleanup deletes them from the refs)
    const hadStreamingStarted = context.streamingStartedRef?.current?.has(threadId) ?? false;
    const isRetry = getIsRetry(context, threadId);

    cleanupPendingResponse(context, threadId);

    // User cancelled scenarios:
    // 1. incomplete with reason 'cancelled' = user clicked stop (no tool running)
    // 2. requires-action with reason 'interrupt' = user clicked stop (tool was running)
    const wasUserCancelled =
      (messageStatus === 'incomplete' && messageStatusReason === 'cancelled') ||
      (messageStatus === 'requires-action' && messageStatusReason === 'interrupt');
    const isSuccessfulComplete = messageStatus === 'complete';

    // Get abort reason from context (set by handleError) or derive from status
    let abortReason = context.abortReasonsRef?.current?.get(threadId);
    if (!abortReason && wasUserCancelled) {
      abortReason = ABORT_REASONS.USER_CANCELLED;
    } else if (!abortReason && messageStatus === 'incomplete') {
      abortReason = ABORT_REASONS.UNKNOWN;
    }

    // Clean up abort reason after reading
    if (context.abortReasonsRef?.current?.has(threadId)) {
      context.abortReasonsRef.current.delete(threadId);
    }

    const streamingDurationMs = Date.now() - startTime;
    const isDocumentHidden =
      typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false;

    if (isSuccessfulComplete) {
      trackEvent({
        event: CHAT_ANALYTICS_EVENTS.RESPONSE_COMPLETED,
        properties: {
          threadId,
          streamingDurationMs,
          isDocumentHidden,
          isRetry,
        },
      });
    } else {
      // Interrupted (aborted, error, user cancelled, etc.)
      const isNetworkOffline = !navigator.onLine;
      const eventData = {
        threadId,
        streamingDurationMs,
        requestDurationMs: streamingDurationMs,
        abortReason,
        isDocumentHidden,
        isNetworkOffline,
        hadStreamingStarted,
        isRetry,
      };

      if (isNetworkOffline) {
        queueEventForLater(CHAT_ANALYTICS_EVENTS.RESPONSE_INTERRUPTED, eventData);
      } else {
        trackEvent({
          event: CHAT_ANALYTICS_EVENTS.RESPONSE_INTERRUPTED,
          properties: eventData,
        });
      }
    }
  }, [messageStatus, messageStatusReason, threadId, context, trackEvent]);
}
